/**
 * Shapefile Service
 * 
 * Handles shapefile CRUD operations through the climate server API.
 * Provides methods for uploading, listing, viewing, and deleting shapefiles.
 */

import { AbstractClimateService } from '../base/AbstractClimateService';
import { ClimateApiError } from '../base/ClimateApiError';
import { ClimateApiErrorType } from '../base/types';
import { getOptimizedClimateConfig } from '../../../config/climate';
import type { 
  Shapefile, 
  ClimateShapefileResponse,
  ShapefileResponse, 
  ShapefileUploadData, 
  ShapefileCoverage 
} from '../../../types/shapefile';

export class ShapefileService extends AbstractClimateService {
  
  constructor() {
    super(getOptimizedClimateConfig());
  }

  /**
   * Get the service-specific path for spatial endpoints
   */
  protected getServicePath(): string {
    return '/spatial';
  }

  /**
   * Map climate server response to frontend interface
   */
  private mapClimateResponse(apiResponse: ClimateShapefileResponse): Shapefile {
    // Map bounds to boundingBox format
    const boundingBox = apiResponse.bounds ? {
      minLat: apiResponse.bounds.min_lat,
      maxLat: apiResponse.bounds.max_lat,
      minLng: apiResponse.bounds.min_lon,
      maxLng: apiResponse.bounds.max_lon,
    } : null;

    return {
      id: apiResponse.id,
      name: apiResponse.name,
      filename: apiResponse.name, // Use name as filename fallback
      uploadedAt: apiResponse.created_at,
      fileSize: 0, // Not provided by API
      geometryType: 'Polygon', // Default, not provided by API
      boundingBox,
      featureCount: apiResponse.feature_count,
      coverage: undefined, // Not provided by API, would need separate call
      isActive: apiResponse.validation_status === 'valid',
      createdAt: apiResponse.created_at,
      updatedAt: apiResponse.updated_at,
      // Additional fields from climate API
      slug: apiResponse.slug,
      description: typeof apiResponse.description === 'string' ? apiResponse.description : undefined,
      regionType: apiResponse.region_type,
      adminLevel: apiResponse.admin_level,
      totalAreaKm2: apiResponse.total_area_km2,
      validationStatus: apiResponse.validation_status,
      spatialFeaturesCount: apiResponse.spatial_features_count,
      spatialFeatures: apiResponse.spatial_features,
    };
  }
  
  /**
   * Get list of shapefiles with filtering and pagination
   */
  async getShapefiles(options: {
    page?: number;
    limit?: number;
    search?: string;
    geometryType?: string;
    isActive?: boolean;
  } = {}): Promise<ShapefileResponse> {
    const {
      page = 1,
      limit = 25,
      search,
      geometryType,
      isActive
    } = options;

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      params.append('search', search);
    }
    if (geometryType && geometryType !== 'all') {
      params.append('geometryType', geometryType);
    }
    if (isActive !== undefined) {
      params.append('isActive', isActive.toString());
    }

    try {
      const response = await this.get<{
        shapefiles: ClimateShapefileResponse[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(`/shapefiles?${params.toString()}`);

      // Map climate server response to frontend interface
      const mappedShapefiles = response.shapefiles.map(shapefile => 
        this.mapClimateResponse(shapefile)
      );

      return {
        shapefiles: mappedShapefiles,
        pagination: response.pagination
      };
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw new ClimateApiError(
        ClimateApiErrorType.SERVER_ERROR,
        'Failed to fetch shapefiles',
        undefined,
        error
      );
    }
  }

  /**
   * Get shapefile details by ID
   */
  async getShapefileById(id: string): Promise<Shapefile> {
    if (!id) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'Shapefile ID is required'
      );
    }

    try {
      // Try to get response - it might be wrapped or direct
      const response = await this.get<ClimateShapefileResponse | { shapefile: ClimateShapefileResponse }>(`/shapefiles/${id}`);
      
      // Handle both wrapped and direct response formats
      let shapefileData: ClimateShapefileResponse;
      if ('shapefile' in response) {
        // Wrapped format: { shapefile: {...} }
        shapefileData = response.shapefile;
      } else if ('id' in response) {
        // Direct format: { id, name, ... }
        shapefileData = response;
      } else {
        throw new Error('Invalid response format from climate API');
      }
      
      return this.mapClimateResponse(shapefileData);
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw new ClimateApiError(
        ClimateApiErrorType.SERVER_ERROR,
        `Failed to fetch shapefile ${id}`,
        undefined,
        error
      );
    }
  }

  /**
   * Upload a new shapefile
   */
  async uploadShapefile(data: ShapefileUploadData): Promise<Shapefile> {
    if (!data.file) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'Shapefile is required'
      );
    }

    if (!data.name?.trim()) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'Shapefile name is required'
      );
    }

    // Validate file type
    if (!data.file.name.toLowerCase().endsWith('.zip')) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'Only ZIP files containing shapefiles are supported'
      );
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (data.file.size > maxSize) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'File size exceeds 50MB limit'
      );
    }

    const formData = new FormData();
    formData.append('name', data.name.trim());
    formData.append('file', data.file);

    try {
      // Use the correct upload endpoint from OpenAPI spec
      const response = await this.request<{ shapefile: ClimateShapefileResponse }>('/shapefiles/upload', {
        method: 'POST',
        body: formData,
        timeout: 60000, // Extended timeout for file upload
        headers: {
          // Don't set Content-Type for FormData - let browser set it with boundary
        }
      });
      return this.mapClimateResponse(response.shapefile);
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw new ClimateApiError(
        ClimateApiErrorType.SERVER_ERROR,
        'Failed to upload shapefile',
        undefined,
        error
      );
    }
  }

  /**
   * Delete a shapefile
   */
  async deleteShapefile(id: string): Promise<void> {
    if (!id) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'Shapefile ID is required'
      );
    }

    try {
      await this.delete<{ success: boolean }>(`/shapefiles/${id}`);
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw new ClimateApiError(
        ClimateApiErrorType.SERVER_ERROR,
        `Failed to delete shapefile ${id}`,
        undefined,
        error
      );
    }
  }

  /**
   * Get climate data coverage for a shapefile
   */
  async getShapefileCoverage(id: string): Promise<ShapefileCoverage> {
    if (!id) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'Shapefile ID is required'
      );
    }

    try {
      const response = await this.get<{ coverage: ShapefileCoverage }>(`/shapefiles/${id}/coverage`);
      return response.coverage;
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw new ClimateApiError(
        ClimateApiErrorType.SERVER_ERROR,
        `Failed to fetch coverage for shapefile ${id}`,
        undefined,
        error
      );
    }
  }

  /**
   * Download a shapefile
   */
  async downloadShapefile(id: string): Promise<Blob> {
    if (!id) {
      throw new ClimateApiError(
        ClimateApiErrorType.VALIDATION_ERROR,
        'Shapefile ID is required'
      );
    }

    try {
      // For download, we need to handle the response as a blob
      const url = `${this.config.baseUrl}${this.getServicePath()}/shapefiles/${id}/download`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        },
        signal: AbortSignal.timeout(30000) // Extended timeout for download
      });

      if (!response.ok) {
        throw new ClimateApiError(
          ClimateApiErrorType.SERVER_ERROR,
          `Download failed: ${response.status} ${response.statusText}`,
          response.status
        );
      }

      return await response.blob();
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      throw new ClimateApiError(
        ClimateApiErrorType.SERVER_ERROR,
        `Failed to download shapefile ${id}`,
        undefined,
        error
      );
    }
  }
}

// Export a singleton instance
export const shapefileService = new ShapefileService(); 